import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
var PICKER_OFFSET = 6; // px outset on each side
var buildCandidates = function (manipulative, focusedCc) {
    var _a, _b;
    var others = manipulative
        .filter(function (m) {
        var _a;
        return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coinsAndCounters' &&
            m.data.id !== focusedCc.id &&
            // Exclude other members of the focused counter's own group
            !(focusedCc.groupId && m.data.groupId === focusedCc.groupId);
    })
        .map(function (m) { return m.data; });
    var groupMap = new Map();
    var standalones = [];
    for (var _i = 0, others_1 = others; _i < others_1.length; _i++) {
        var cc = others_1[_i];
        if (cc.groupId) {
            var arr = (_a = groupMap.get(cc.groupId)) !== null && _a !== void 0 ? _a : [];
            arr.push(cc);
            groupMap.set(cc.groupId, arr);
        }
        else {
            standalones.push(cc);
        }
    }
    var candidates = [];
    // Groups first
    groupMap.forEach(function (members, groupId) {
        candidates.push({
            kind: 'group',
            groupId: groupId,
            ccIds: members.map(function (m) { return m.id; }),
            centers: members.map(function (m) { return m.center; }),
            dimensions: members.map(function (m) { return m.dimensions; }),
            scales: members.map(function (m) { var _a; return (_a = m.scale) !== null && _a !== void 0 ? _a : 1; }),
            counterTypes: members.map(function (m) { return m.counterType; }),
        });
    });
    // Standalones after
    for (var _c = 0, standalones_1 = standalones; _c < standalones_1.length; _c++) {
        var cc = standalones_1[_c];
        candidates.push({
            kind: 'standalone',
            ccId: cc.id,
            center: cc.center,
            dimensions: cc.dimensions,
            scale: (_b = cc.scale) !== null && _b !== void 0 ? _b : 1,
            counterType: cc.counterType,
        });
    }
    return candidates;
};
var candidateBtnIds = function (c) {
    if (c.kind === 'standalone')
        return [c.ccId];
    return c.ccIds;
};
var CoinsAndCountersLinkPicker = function () {
    var _a;
    var t = useTranslation().t;
    var manipulative = useContext(DataContext).manipulative;
    var _b = useState(null), focusedCcId = _b[0], setFocusedCcId = _b[1];
    var _c = useState(0), activeIdx = _c[0], setActiveIdx = _c[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    // Open: listen for custom event
    useEffect(function () {
        var onOpen = function (data) {
            returnFocusIdRef.current = data.focusedCcId;
            setFocusedCcId(data.focusedCcId);
            setActiveIdx(0);
            announce(t('text.ccPickerOpened'));
        };
        customEventHandler.addEventListener('openCcGroupPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openCcGroupPicker', onOpen); };
    }, [t]);
    var focusedItem = focusedCcId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedCcId; })
        : undefined;
    var focusedCc = ((_a = focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.data) === null || _a === void 0 ? void 0 : _a.type) === 'coinsAndCounters'
        ? focusedItem.data
        : null;
    var candidates = focusedCc ? buildCandidates(manipulative, focusedCc) : [];
    // Auto-close when source deleted or no candidates
    useEffect(function () {
        if (focusedCcId && (!focusedCc || candidates.length === 0)) {
            setFocusedCcId(null);
        }
    }, [focusedCcId, focusedCc, candidates.length]);
    // Click-outside: close picker
    useEffect(function () {
        if (!focusedCcId)
            return;
        var handleOutside = function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))
                return;
            setFocusedCcId(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedCcId]);
    // Focus first candidate button when picker opens (>50ms to beat closeMenu focus-restore)
    useEffect(function () {
        if (!focusedCcId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedCcId]);
    if (!focusedCcId || !focusedCc || candidates.length === 0)
        return null;
    var closePicker = function () {
        setFocusedCcId(null);
        var id = returnFocusIdRef.current;
        if (id) {
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        }
    };
    var onSelect = function (candidate) {
        var _a;
        var sourceId = (_a = focusedCc === null || focusedCc === void 0 ? void 0 : focusedCc.groupId) !== null && _a !== void 0 ? _a : focusedCcId;
        var diceIds = candidate.kind === 'standalone'
            ? [sourceId, candidate.ccId]
            : [candidate.groupId, sourceId];
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'groupDiceFromMenu',
            panel: 'bottomPanel',
            diceIds: diceIds,
        });
        setFocusedCcId(null);
    };
    var moveCandidateFocus = function (delta) {
        var next = (activeIdx + delta + candidates.length) % candidates.length;
        setActiveIdx(next);
        var cand = candidates[next];
        var targetId = cand.kind === 'standalone' ? cand.ccId : cand.ccIds[0];
        requestAnimationFrame(function () {
            var _a;
            var btn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button.ccLinkPicker__target[data-btn-id=\"".concat(targetId, "\"]"));
            btn === null || btn === void 0 ? void 0 : btn.focus();
        });
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            return;
        }
        if (e.key === 'ArrowRight' ||
            e.key === 'ArrowDown' ||
            (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(1);
        }
        else if (e.key === 'ArrowLeft' ||
            e.key === 'ArrowUp' ||
            (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(-1);
        }
    };
    var getCandidateIdx = function (btnId) {
        return candidates.findIndex(function (c) { return candidateBtnIds(c).includes(btnId); });
    };
    var onBtnFocus = function (btnId) {
        var idx = getCandidateIdx(btnId);
        if (idx !== -1)
            setActiveIdx(idx);
    };
    var btnCounter = 0;
    return (_jsx("div", { ref: containerRef, className: "ccLinkPicker", role: "dialog", "aria-modal": "true", "aria-label": t('text.ccPickerLabel'), onKeyDown: onContainerKeyDown, children: candidates.map(function (cand, candIdx) {
            var isActive = candIdx === activeIdx;
            var isFirstOverall = btnCounter === 0;
            btnCounter++;
            if (cand.kind === 'standalone') {
                var w = cand.dimensions.width * cand.scale;
                var h = cand.dimensions.height * cand.scale;
                var left = cand.center.x - w / 2;
                var top_1 = cand.center.y - h / 2;
                var ariaLabel_1 = t('text.ccPickerTarget', {
                    counterType: t("text.coinsAndCountersTypeAria_".concat(cand.counterType), {
                        defaultValue: cand.counterType,
                    }),
                });
                return (_jsx("button", { ref: isFirstOverall ? firstBtnRef : undefined, type: "button", className: "ccLinkPicker__target".concat(isActive ? ' ccLinkPicker__target--active' : ''), "data-btn-id": cand.ccId, "data-cand-idx": candIdx, tabIndex: 0, style: {
                        position: 'absolute',
                        left: left - PICKER_OFFSET,
                        top: top_1 - PICKER_OFFSET,
                        width: w + PICKER_OFFSET * 2,
                        height: h + PICKER_OFFSET * 2,
                        zIndex: 99999,
                    }, "aria-label": ariaLabel_1, onFocus: function () { return onBtnFocus(cand.ccId); }, onClick: function () { return onSelect(cand); } }, "".concat(cand.ccId, "-standalone")));
            }
            // Group candidate — render ONE button over the bounding box of all members
            // so the user sees a single target for the whole group, not per-member buttons.
            var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            cand.centers.forEach(function (center, i) {
                var _a, _b;
                var w = cand.dimensions[i].width * ((_a = cand.scales[i]) !== null && _a !== void 0 ? _a : 1);
                var h = cand.dimensions[i].height * ((_b = cand.scales[i]) !== null && _b !== void 0 ? _b : 1);
                minX = Math.min(minX, center.x - w / 2);
                minY = Math.min(minY, center.y - h / 2);
                maxX = Math.max(maxX, center.x + w / 2);
                maxY = Math.max(maxY, center.y + h / 2);
            });
            var ariaLabel = t('text.ccPickerGroupTarget', { count: cand.ccIds.length });
            return (_jsx("button", { ref: isFirstOverall ? firstBtnRef : undefined, type: "button", className: "ccLinkPicker__target".concat(isActive ? ' ccLinkPicker__target--active' : ''), "data-btn-id": cand.ccIds[0], "data-cand-idx": candIdx, tabIndex: 0, style: {
                    position: 'absolute',
                    left: minX - PICKER_OFFSET,
                    top: minY - PICKER_OFFSET,
                    width: maxX - minX + PICKER_OFFSET * 2,
                    height: maxY - minY + PICKER_OFFSET * 2,
                    zIndex: 99999,
                }, "aria-label": ariaLabel, onFocus: function () { return onBtnFocus(cand.ccIds[0]); }, onClick: function () { return onSelect(cand); } }, "".concat(cand.groupId, "-group")));
        }) }));
};
export default CoinsAndCountersLinkPicker;
